import React, { useState, useMemo, useEffect } from 'react';
import { HiChevronDown, HiCollection, HiEye, HiEyeOff, HiX } from 'react-icons/hi';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ParameterTooltip } from '../utils/distributionParameters';
import { formatDataDeDias } from '../utils/dateFromDays';
import SpecifyDayPanel from './SpecifyDayPanel';
import apiService from '../services/apiService';

const SimulationTab = ({ data, dataReferencia: dataReferenciaProp, setDataReferencia: setDataReferenciaProp }) => {
    const [viewMode, setViewMode] = useState('TABELA');
    const [dataReferenciaLocal, setDataReferenciaLocal] = useState('');
    const dataReferencia = dataReferenciaProp !== undefined ? dataReferenciaProp : dataReferenciaLocal;
    const setDataReferencia = setDataReferenciaProp || setDataReferenciaLocal;
    const [mostrarPainelData, setMostrarPainelData] = useState(false);

    // Extract simulation data from backend (format: Simulador.agregadoTRL / agregadoCRL / agregadoConjunta)
    const simulationData = useMemo(() => {
        if (!data || !data.Simulador) {
            return { trl: null, crl: null, conjunta: null };
        }

        return {
            trl: data.Simulador.agregadoTRL || null,
            crl: data.Simulador.agregadoCRL || null,
            conjunta: data.Simulador.agregadoConjunta || null
        };
    }, [data]);

    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="p-6 text-center text-gray-500">
                <p>Nenhum dado disponível para simulação</p>
            </div>
        );
    }

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        if (typeof value !== 'number') return value;

        // Usar notação científica para valores muito pequenos (mas não zero)
        if (Math.abs(value) < 0.01 && value !== 0) {
            return value.toExponential(2);
        }

        return value.toFixed(2);
    };

    // Concatena valor em dias com data convertida (quando dataReferencia setado).
    const formatNumDate = (value) => {
        const num = formatNumber(value);
        const dt = formatDataDeDias(value, dataReferencia);
        return dt ? `${num} (${dt})` : num;
    };

    // Componente para exibir mensagens de erro quando simulacao é string
    const ErrorMessage = ({ message, title, type }) => {
        return (
            <div className="bg-white border border-red-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
                <div className="text-center text-red-600 py-8">
                    <HiCollection className="w-16 h-16 mx-auto mb-4 text-red-300" />
                    <p className="text-base font-medium mb-2">Erro na simulação {type}</p>
                    <p className="text-sm text-gray-700 max-w-md mx-auto">{message}</p>
                </div>
            </div>
        );
    };

    // Tabela de transições individuais
    const TransitionsTable = ({ stats, title, type }) => {
        // Estado local: cada tabela (TRL/CRL/Conjunta) tem seu próprio percentil.
        const [percentilInput, setPercentilInput] = useState('75');
        const [percentilCustom, setPercentilCustom] = useState(null);
        const [calculandoPercentil, setCalculandoPercentil] = useState(false);
        const [erroPercentil, setErroPercentil] = useState(null);

        // Reseta percentil ao trocar de análise.
        useEffect(() => {
            setPercentilInput('75');
            setPercentilCustom(null);
            setErroPercentil(null);
        }, [stats]);

        const handleCalcularPercentil = async () => {
            const p = parseFloat(percentilInput);
            if (isNaN(p) || p < 0 || p > 100) {
                setErroPercentil('Percentil deve estar entre 0 e 100');
                return;
            }
            if (!data?.Simulador) return;
            setErroPercentil(null);
            setCalculandoPercentil(true);
            try {
                const resp = await apiService.calcularPercentilSimulacao(data.Simulador, p);
                if (resp?.erro) {
                    setErroPercentil(resp.erro);
                } else {
                    const bloco = type === 'TRL' ? (resp.trl || {})
                        : type === 'CRL' ? (resp.crl || {})
                        : (resp.conjunta || {});
                    setPercentilCustom(bloco);
                }
            } catch (e) {
                setErroPercentil(e?.message || 'Erro ao calcular percentil');
            } finally {
                setCalculandoPercentil(false);
            }
        };

        if (!stats || !stats.simulacao) {
            return (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
                    <div className="text-center text-gray-500 py-12">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado disponível para {type}</p>
                    </div>
                </div>
            );
        }

        // Verificar se simulacao é uma string (erro)
        if (typeof stats.simulacao === 'string') {
            return <ErrorMessage message={stats.simulacao} title={title} type={type} />;
        }

        const keys = Object.keys(stats.simulacao);
        const isConjunta = keys.length === 2 && keys.includes('TRL') && keys.includes('CRL');

        const transitions = isConjunta
            ? []
            : keys.sort((a, b) => Number(a) - Number(b));
        const ini = transitions.length > 0 ? Number(transitions[0]) : null;

        const valorPercentilTransicao = (key) => {
            if (percentilCustom && percentilCustom[String(key)] !== undefined) return percentilCustom[String(key)];
            return stats.p75?.[key];
        };
        const valorPercentilConjunta = () => {
            if (percentilCustom?.duracao !== undefined && percentilCustom?.duracao !== null) return percentilCustom.duracao;
            return stats.duracaoP75;
        };

        return (
            <div className="overflow-x-auto">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
                <table className="min-w-full divide-y divide-gray-200" style={{ outline: 'none' }}>
                    <thead className="bg-gray-50">
                        <tr style={{ outline: 'none' }}>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {isConjunta ? 'Métrica' : `Transição ${type}`}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Média (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P10 (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P50 (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P90 (dia)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <div className="flex items-center gap-1">
                                    <span>P</span>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="1"
                                        value={percentilInput}
                                        onChange={(e) => { setPercentilInput(e.target.value); setErroPercentil(null); }}
                                        onKeyDown={(e) => e.key === 'Enter' && handleCalcularPercentil()}
                                        className="border border-gray-300 rounded px-1 py-0.5 text-xs w-14 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 normal-case"
                                    />
                                    <button
                                        onClick={handleCalcularPercentil}
                                        disabled={calculandoPercentil || !data?.Simulador}
                                        style={{ backgroundColor: '#003B6B' }}
                                        className="text-white font-semibold py-0.5 px-2 rounded transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-xs normal-case"
                                    >
                                        {calculandoPercentil ? '...' : 'Calcular'}
                                    </button>
                                    {erroPercentil && (
                                        <span className="ml-2 text-xs text-red-600 normal-case">{erroPercentil}</span>
                                    )}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {!isConjunta && transitions.map((key, index) => {
                            const nextLevel = Number(key) + 1;
                            return (
                                <tr key={index} style={{ outline: 'none' }}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {ini}-{nextLevel}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumDate(stats.media[key])}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumDate(stats.p10[key])}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumDate(stats.p50[key])}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumDate(stats.p90[key])}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumDate(valorPercentilTransicao(key))}
                                    </td>
                                </tr>
                            );
                        })}
                        {isConjunta && (
                            <tr className="bg-blue-50 font-semibold" style={{ outline: 'none' }}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                    Duração Total
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatNumDate(stats.duracaoMedia)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatNumDate(stats.duracaoP10)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatNumDate(stats.duracaoP50)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatNumDate(stats.duracaoP90)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatNumDate(valorPercentilConjunta())}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    // Legenda customizada dentro do gráfico
    const CustomLegend = ({ media, p10, p50, p90 }) => {
        return (
            <div style={{
                position: 'absolute',
                top: '30px',
                right: '40px',
                padding: '10px',
                fontSize: '12px',
                lineHeight: '1.6',
                zIndex: 10
            }}>
                <div style={{ color: '#eab308' }}>--- Média = {formatNumDate(media)}</div>
                <div style={{ color: '#10b981' }}>--- P10 = {formatNumDate(p10)}</div>
                <div style={{ color: '#000' }}>--- P50 = {formatNumDate(p50)}</div>
                <div style={{ color: '#dc2626' }}>--- P90 = {formatNumDate(p90)}</div>
            </div>
        );
    };


    // Tooltip customizado dos histogramas — anexa a data quando dataReferencia setado.
    const HistogramTooltip = ({ active, payload, valueLabel }) => {
        if (!active || !payload || payload.length === 0) return null;
        const item = payload[0];
        const tempo = item.payload?.tempo;
        const dt = formatDataDeDias(tempo, dataReferencia);
        return (
            <div style={{
                backgroundColor: 'white',
                border: '1px solid #ccc',
                padding: '8px 10px',
                borderRadius: '4px',
                fontSize: '13px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <div><strong>Tempo:</strong> {formatNumber(tempo)} dias{dt ? ` (${dt})` : ''}</div>
                <div><strong>{valueLabel}:</strong> {formatNumber(item.value)}</div>
            </div>
        );
    };

    // Gráfico de histograma com percentis (Duração Total)
    const DurationHistogram = ({ stats, title, type }) => {
        const chartData = useMemo(() => {
            if (!stats || !stats.duracao || !Array.isArray(stats.duracao)) {
                return [];
            }
            const duracaoValues = stats.duracao;
            const minVal = Math.min(...duracaoValues);
            const maxVal = Math.max(...duracaoValues);
            const numBins = 20;
            const binWidth = (maxVal - minVal) / numBins;

            const bins = Array(numBins).fill(0).map((_, i) => ({
                start: minVal + i * binWidth,
                end: minVal + (i + 1) * binWidth,
                count: 0
            }));

            duracaoValues.forEach(val => {
                const binIndex = Math.min(Math.floor((val - minVal) / binWidth), numBins - 1);
                if (binIndex >= 0 && binIndex < numBins) {
                    bins[binIndex].count++;
                }
            });

            // Normalizar para densidade
            const totalCount = duracaoValues.length;
            return bins.map((bin) => ({
                tempo: Math.round((bin.start + bin.end) / 2),
                densidade: bin.count / totalCount / binWidth,
                count: bin.count
            }));
        }, [stats]);

        // Encontrar índices mais próximos dos valores de referência para as linhas
        const findNearestIndex = useMemo(() => {
            return (value) => {
                if (chartData.length === 0) return 0;
                let nearestIndex = 0;
                let minDiff = Math.abs(chartData[0].tempo - value);
                chartData.forEach((item, index) => {
                    const diff = Math.abs(item.tempo - value);
                    if (diff < minDiff) {
                        minDiff = diff;
                        nearestIndex = index;
                    }
                });
                return nearestIndex;
            };
        }, [chartData]);

        // Verificar se simulacao é uma string (erro) - APÓS todos os hooks
        if (stats && stats.simulacao && typeof stats.simulacao === 'string') {
            return <ErrorMessage message={stats.simulacao} title={title} type={type} />;
        }

        if (!stats || !stats.duracao || !Array.isArray(stats.duracao) || chartData.length === 0) {
            return null;
        }

        return (
            <div className="bg-white rounded-lg p-6 mb-6" style={{ outline: 'none', border: 'none' }}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
                <div style={{ position: 'relative', outline: 'none', border: 'none' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                    <CustomLegend
                        media={stats.duracaoMedia}
                        p10={stats.duracaoP10}
                        p50={stats.duracaoP50}
                        p90={stats.duracaoP90}
                    />
                    <ResponsiveContainer width="100%" height={500}>
                        <BarChart data={chartData} margin={{ top: 20, right: 20, left: 80, bottom: 50 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="tempo"
                                label={{ value: 'Tempo [dias]', position: 'insideBottom', offset: -10 }}
                            />
                            <YAxis label={{ value: 'Densidade', angle: -90, position: 'center', dx: -50 }} />
                            <Tooltip content={<HistogramTooltip valueLabel="Densidade" />} />
                            <Bar dataKey="densidade" fill="#4169e1" isAnimationActive={false} />
                            <ReferenceLine x={chartData[findNearestIndex(stats.duracaoMedia)]?.tempo} stroke="#eab308" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(stats.duracaoP10)]?.tempo} stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(stats.duracaoP50)]?.tempo} stroke="#000" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(stats.duracaoP90)]?.tempo} stroke="#dc2626" strokeWidth={2} strokeDasharray="5 5" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    // Gráfico de distribuição ajustada - Mostra PDF e CDF com duplo eixo Y
    const FittedDistributionChart = ({ stats, title, type, transitionKey }) => {
        const [showPDF, setShowPDF] = useState(true);
        const [showCDF, setShowCDF] = useState(true);
        const [pontoMarcado, setPontoMarcado] = useState(null);

        // Quando transitionKey é fornecido (TRL/CRL por degrau), o ajuste e a
        // distribuição-fonte são indexados por degrau; caso contrário (Conjunta)
        // usamos o ajuste único e a chave duracao.
        const ajusteAtual = transitionKey != null
            ? (stats && stats.ajuste ? stats.ajuste[transitionKey] : null)
            : (stats ? stats.ajuste : null);
        const fonteValores = transitionKey != null
            ? (stats && stats.simulacao && typeof stats.simulacao !== 'string'
                ? stats.simulacao[transitionKey]
                : null)
            : (stats ? stats.duracao : null);

        const theoreticalData = useMemo(() => {
            if (!ajusteAtual || !ajusteAtual.distMelhor) {
                return [];
            }

            const { pdfMelhor, cdfMelhor } = ajusteAtual;

            if (pdfMelhor && Array.isArray(pdfMelhor) && pdfMelhor.length > 0) {
                const numPoints = pdfMelhor.length;

                // Determinar range baseado nos dados-fonte
                let minVal = 0;
                let maxVal = 500;

                if (fonteValores && Array.isArray(fonteValores) && fonteValores.length > 0) {
                    minVal = 0;
                    maxVal = Math.max(...fonteValores);
                }

                // Mapear arrays para dados do gráfico
                return pdfMelhor.map((densidade, index) => {
                    const x = minVal + (index / (numPoints - 1)) * (maxVal - minVal);
                    return {
                        tempo: x,
                        densidade: densidade,
                        cdf: cdfMelhor && cdfMelhor[index] ? cdfMelhor[index] : null
                    };
                });
            }

            return [];
        }, [ajusteAtual, fonteValores]);

        // Verificar se simulacao é uma string (erro) - APÓS todos os hooks
        if (stats && stats.simulacao && typeof stats.simulacao === 'string') {
            return <ErrorMessage message={stats.simulacao} title={title} type={type} />;
        }

        if (!ajusteAtual || !ajusteAtual.distMelhor || theoreticalData.length === 0) {
            return null;
        }

        const { distribuicao, params } = ajusteAtual.distMelhor;

        return (
            <div className="bg-white rounded-lg p-6 mb-6" style={{ outline: 'none', border: 'none' }}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
                <SpecifyDayPanel
                    distribuicao={distribuicao}
                    params={params}
                    showPDF={showPDF}
                    onPontoMarcado={setPontoMarcado}
                    dataReferencia={dataReferencia}
                />
                <div style={{ position: 'relative', outline: 'none', border: 'none' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                    {/* Legenda customizada */}
                    <div style={{
                        position: 'absolute',
                        top: '15px',
                        right: '170px',
                        padding: '10px',
                        fontSize: '12px',
                        lineHeight: '1.6',
                        zIndex: 10,
                        color: '#000',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        borderRadius: '4px'
                    }}>
                        <div><strong>Distribuição ({distribuicao})</strong></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <ParameterTooltip distribution={distribuicao} parameters={params}>
                                Parâmetros: {params.map(p => formatNumber(p)).join(', ')}
                            </ParameterTooltip>
                        </div>
                        <div>p_valor (KS) = {formatNumber(ajusteAtual.distMelhor.ksP)}</div>

                        <div style={{ marginTop: '10px', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>
                            <div
                                style={{
                                    cursor: 'pointer',
                                    opacity: showPDF ? 1 : 0.5,
                                    color: showPDF ? 'black' : '#9ca3af',
                                    marginBottom: '2px',
                                    userSelect: 'none'
                                }}
                                onClick={() => setShowPDF(!showPDF)}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    {showPDF ? <HiEye /> : <HiEyeOff />}
                                    <label>Densidade (PDF)</label>
                                </div>

                            </div>
                            <div
                                style={{
                                    cursor: 'pointer',
                                    opacity: showCDF ? 1 : 0.5,
                                    color: showCDF ? 'black' : '#9ca3af',
                                    userSelect: 'none'
                                }}
                                onClick={() => setShowCDF(!showCDF)}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    {showCDF ? <HiEye /> : <HiEyeOff />}
                                    Probabilidade Acumulada (CDF)
                                </div>
                            </div>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={500}>
                        <LineChart data={theoreticalData} margin={{ top: 20, right: 100, left: 80, bottom: 50 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="tempo"
                                type="number"
                                domain={['dataMin', 'dataMax']}
                                label={{ value: 'Tempo [dias]', position: 'insideBottom', offset: -10 }}
                            />
                            {showPDF ?
                                <YAxis
                                    yAxisId="pdf"
                                    orientation="left"
                                    label={{ value: 'Densidade (PDF)', angle: -90, position: 'center', dx: -50, fill: '#2563eb' }}
                                    // tickFormatter={(value) => {
                                    //     if (Math.abs(value) < 0.001 && value !== 0) {
                                    //         return value.toExponential(1);
                                    //     }
                                    //     if (Math.abs(value) > 1000) {
                                    //         return value.toExponential(1);
                                    //     }
                                    //     return value.toFixed(3);
                                    // }}
                                /> : null}
                            {showCDF ?
                                <YAxis
                                    yAxisId="cdf"
                                    orientation="right"
                                    domain={[0, 1]}
                                    label={{ value: 'Probabilidade Acumulada (CDF)', angle: 90, position: 'center', dx: 50, fill: '#dc2626' }}
                                    tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                                /> : null}
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length > 0) {
                                        const data = payload[0].payload;
                                        const tempo = data.tempo;
                                        const densidade = data.densidade;
                                        const cdf = data.cdf;

                                        return (
                                            <div style={{
                                                backgroundColor: 'white',
                                                border: '1px solid #ccc',
                                                padding: '10px',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                            }}>
                                                <div>
                                                    <strong>Valor (X):</strong> {formatNumber(tempo)} dias
                                                    {formatDataDeDias(tempo, dataReferencia) && ` (${formatDataDeDias(tempo, dataReferencia)})`}
                                                </div>
                                                {showPDF && (
                                                    <div>
                                                        <strong>Densidade (PDF):</strong> {formatNumber(densidade)}
                                                    </div>
                                                )}
                                                {showCDF && cdf !== null && (
                                                    <div >
                                                        <strong>Probabilidade Acumulada (CDF):</strong> {(cdf * 100).toFixed(1)}%
                                                    </div>
                                                )}
                                                {!showPDF && !showCDF && (
                                                    <div style={{ color: '#6b7280', fontStyle: 'italic' }}>
                                                        Ative uma curva na legenda
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            {showPDF && (
                                <Line
                                    type="monotone"
                                    dataKey="densidade"
                                    yAxisId="pdf"
                                    stroke="#2563eb"
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={false}
                                    isAnimationActive={false}
                                    name="PDF"
                                />
                            )}
                            {showCDF && (
                                <Line
                                    type="monotone"
                                    dataKey="cdf"
                                    yAxisId="cdf"
                                    stroke="#dc2626"
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={false}
                                    isAnimationActive={false}
                                    name="CDF"
                                />
                            )}
                            {pontoMarcado && pontoMarcado.cdf !== null && (
                                <ReferenceLine
                                    x={pontoMarcado.valor}
                                    yAxisId={showPDF ? 'pdf' : 'cdf'}
                                    stroke="#16a34a"
                                    strokeWidth={2}
                                    strokeDasharray="4 4"
                                    label={(props) => {
                                        const { viewBox } = props;
                                        return (
                                            <g>
                                                <text x={viewBox.x} y={viewBox.y - 5} fill="#16a34a" fontSize={12} textAnchor="middle">
                                                    {`${(pontoMarcado.cdf * 100).toFixed(1)}%`}
                                                </text>
                                                <text x={viewBox.x} y={viewBox.y + viewBox.height + 15} fill="#16a34a" fontSize={12} textAnchor="middle">
                                                    {formatDataDeDias(pontoMarcado.valor, dataReferencia)
                                                        ? `${formatNumber(pontoMarcado.valor)} (${formatDataDeDias(pontoMarcado.valor, dataReferencia)})`
                                                        : `${formatNumber(pontoMarcado.valor)}`}
                                                </text>
                                            </g>
                                        );
                                    }}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };


    // Gráfico de histograma para transições individuais
    const TransitionHistogram = ({ stats, type, transitionKey }) => {
        const chartData = useMemo(() => {
            if (!stats || !stats.simulacao || typeof stats.simulacao === 'string' || !stats.simulacao[transitionKey]) {
                return [];
            }
            const simValues = stats.simulacao[transitionKey];
            const minVal = Math.min(...simValues);
            const maxVal = Math.max(...simValues);
            const numBins = 20;
            const binWidth = (maxVal - minVal) / numBins;

            const bins = Array(numBins).fill(0).map((_, i) => ({
                start: minVal + i * binWidth,
                end: minVal + (i + 1) * binWidth,
                count: 0
            }));

            simValues.forEach(val => {
                const binIndex = Math.min(Math.floor((val - minVal) / binWidth), numBins - 1);
                if (binIndex >= 0 && binIndex < numBins) {
                    bins[binIndex].count++;
                }
            });

            return bins.map((bin) => ({
                tempo: Math.round((bin.start + bin.end) / 2),
                frequencia: bin.count
            }));
        }, [stats, transitionKey]);

        const nextLevel = Number(transitionKey) + 1;
        const ini = stats && stats.simulacao && typeof stats.simulacao !== 'string'
            ? Math.min(...Object.keys(stats.simulacao).map(Number))
            : Number(transitionKey);

        // Encontrar índices mais próximos dos valores de referência para as linhas
        const findNearestIndex = useMemo(() => {
            return (value) => {
                if (chartData.length === 0) return 0;
                let nearestIndex = 0;
                let minDiff = Math.abs(chartData[0].tempo - value);
                chartData.forEach((item, index) => {
                    const diff = Math.abs(item.tempo - value);
                    if (diff < minDiff) {
                        minDiff = diff;
                        nearestIndex = index;
                    }
                });
                return nearestIndex;
            };
        }, [chartData]);

        if (!stats || !stats.simulacao || typeof stats.simulacao === 'string' || !stats.simulacao[transitionKey] || chartData.length === 0) {
            return null;
        }

        return (
            <div className="bg-white rounded-lg p-6 mb-6" style={{ outline: 'none', border: 'none' }}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Simulação da Transição de {type} {ini}-{nextLevel}
                </h3>
                <div style={{ position: 'relative', outline: 'none', border: 'none' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                    <CustomLegend
                        media={stats.media[transitionKey]}
                        p10={stats.p10[transitionKey]}
                        p50={stats.p50[transitionKey]}
                        p90={stats.p90[transitionKey]}
                    />
                    <ResponsiveContainer width="100%" height={500}>
                        <BarChart data={chartData} margin={{ top: 20, right: 20, left: 80, bottom: 50 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="tempo"
                                label={{ value: 'Tempo [dias]', position: 'insideBottom', offset: -10 }}
                            />
                            <YAxis label={{ value: 'Frequência', angle: -90, position: 'center', dx: -50 }} />
                            <Tooltip content={<HistogramTooltip valueLabel="Frequência" />} />
                            <Bar dataKey="frequencia" fill="#4169e1" isAnimationActive={false} />
                            <ReferenceLine x={chartData[findNearestIndex(stats.media[transitionKey])]?.tempo} stroke="#eab308" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(stats.p10[transitionKey])]?.tempo} stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(stats.p50[transitionKey])]?.tempo} stroke="#000" strokeWidth={2} strokeDasharray="5 5" />
                            <ReferenceLine x={chartData[findNearestIndex(stats.p90[transitionKey])]?.tempo} stroke="#dc2626" strokeWidth={2} strokeDasharray="5 5" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 h-full overflow-auto custom-scrollbar">
            {/* Barra superior: modo de visualização + data de referência */}
            <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
                <div className="relative inline-block">
                    <select
                        value={viewMode}
                        onChange={(e) => setViewMode(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded px-4 py-2 pr-8 text-sm text-gray-700 focus:border-gray-400 min-w-40"
                    >
                        <option value="TABELA">TABELA</option>
                        <option value="GRAFICOS">GRÁFICOS</option>
                    </select>
                    <HiChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>

                <div className="flex items-center gap-2">
                    {mostrarPainelData ? (
                        <>
                            <input
                                type="date"
                                value={dataReferencia}
                                onChange={(e) => setDataReferencia(e.target.value)}
                                className="border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            />
                            {dataReferencia && (
                                <button
                                    onClick={() => setDataReferencia('')}
                                    title="Limpar data de referência"
                                    className="text-gray-500 hover:text-red-600 p-1 rounded"
                                >
                                    <HiX className="w-4 h-4" />
                                </button>
                            )}
                            <button
                                onClick={() => setMostrarPainelData(false)}
                                className="text-gray-600 font-medium py-1 px-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition-all duration-200 text-sm"
                            >
                                Fechar
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={() => setMostrarPainelData(true)}
                            style={{ backgroundColor: '#003B6B' }}
                            className="text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg hover:opacity-90 text-sm"
                        >
                            {dataReferencia
                                ? `Data de referência: ${dataReferencia.split('-').reverse().join('/')}`
                                : 'Especificar data de referência'}
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
                {viewMode === 'TABELA' ? (
                    <>
                        <TransitionsTable
                            title="Simulação por Transição - TRL"
                            stats={simulationData.trl}
                            type="TRL"
                        />
                        <TransitionsTable
                            title="Simulação por Transição - CRL"
                            stats={simulationData.crl}
                            type="CRL"
                        />
                        <TransitionsTable
                            title="Simulação Conjunta (TRL + CRL)"
                            stats={simulationData.conjunta}
                            type="Conjunta"
                        />
                    </>
                ) : (
                    <>
                        {/* Gráficos TRL */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                                Simulação TRL
                            </h2>

                            {/* Gráficos de todas as transições TRL (histograma + ajuste por degrau) */}
                            {simulationData.trl && simulationData.trl.simulacao && typeof simulationData.trl.simulacao !== 'string' ?
                                Object.keys(simulationData.trl.simulacao).sort((a, b) => Number(a) - Number(b)).map(key => {
                                    const ini = Math.min(...Object.keys(simulationData.trl.simulacao).map(Number));
                                    const fim = Number(key) + 1;
                                    return (
                                        <React.Fragment key={`trl-${key}`}>
                                            <TransitionHistogram
                                                stats={simulationData.trl}
                                                type="TRL"
                                                transitionKey={key}
                                            />
                                            <FittedDistributionChart
                                                title={`Distribuição Ajustada da Transição de TRL ${ini}-${fim}`}
                                                stats={simulationData.trl}
                                                type="TRL"
                                                transitionKey={key}
                                            />
                                        </React.Fragment>
                                    );
                                })
                                : simulationData.trl && simulationData.trl.simulacao && typeof simulationData.trl.simulacao === 'string' ?
                                    <ErrorMessage
                                        message={simulationData.trl.simulacao}
                                        title="Gráficos de Transições TRL"
                                        type="TRL"
                                    />
                                    : null
                            }
                        </div>

                        {/* Gráficos CRL */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-green-500 pb-2">
                                Simulação CRL
                            </h2>

                            {/* Gráficos de todas as transições CRL (histograma + ajuste por degrau) */}
                            {simulationData.crl && simulationData.crl.simulacao && typeof simulationData.crl.simulacao !== 'string' ?
                                Object.keys(simulationData.crl.simulacao).sort((a, b) => Number(a) - Number(b)).map(key => {
                                    const ini = Math.min(...Object.keys(simulationData.crl.simulacao).map(Number));
                                    const fim = Number(key) + 1;
                                    return (
                                        <React.Fragment key={`crl-${key}`}>
                                            <TransitionHistogram
                                                stats={simulationData.crl}
                                                type="CRL"
                                                transitionKey={key}
                                            />
                                            <FittedDistributionChart
                                                title={`Distribuição Ajustada da Transição de CRL ${ini}-${fim}`}
                                                stats={simulationData.crl}
                                                type="CRL"
                                                transitionKey={key}
                                            />
                                        </React.Fragment>
                                    );
                                })
                                : simulationData.crl && simulationData.crl.simulacao && typeof simulationData.crl.simulacao === 'string' ?
                                    <ErrorMessage
                                        message={simulationData.crl.simulacao}
                                        title="Gráficos de Transições CRL"
                                        type="CRL"
                                    />
                                    : null
                            }
                        </div>

                        {/* Simulação Conjunta TRL + CRL */}
                        {simulationData.conjunta && (
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-purple-500 pb-2">
                                    Simulação Conjunta (TRL + CRL)
                                </h2>

                                {/* Verificar se há erro na simulação conjunta */}
                                {typeof simulationData.conjunta.simulacao === 'string' ?
                                    <ErrorMessage
                                        message={simulationData.conjunta.simulacao}
                                        title="Simulação Conjunta"
                                        type="TRL + CRL"
                                    />
                                    : (
                                        <>
                                            {/* Gráfico de duração total conjunta */}
                                            <DurationHistogram
                                                title="Distribuição da Duração Total do Projeto - TRL + CRL"
                                                stats={simulationData.conjunta}
                                                type="Conjunta (TRL + CRL)"
                                            />

                                            {/* Gráfico de distribuição ajustada conjunta */}
                                            <FittedDistributionChart
                                                title="Distribuição Ajustada da Duração Total do Projeto - TRL + CRL"
                                                stats={simulationData.conjunta}
                                                type="Conjunta (TRL + CRL)"
                                            />
                                        </>
                                    )
                                }
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SimulationTab;
